/**
 * System: Spieler — Bewegung, Regeneration, Stärkungen.
 *
 * Zuständigkeit: alles, was mit der Spielfigur selbst passiert, ohne dass ein
 * Gegner beteiligt ist.
 */

import { TAKT_DAUER } from '@pg/protocol';
import type { Vek } from '@pg/protocol';
import type { Spieler, Welt } from '../typen.js';
import type { Ctx } from './typen.js';
import { begehbar } from '../karte.js';
import { laenge, vek, winkelVon } from '../vektor.js';
import { tempo } from '../tempo.js';

/**
 * Bewegung mit Rutschen an Wänden — sonst bleibt man an jeder Ecke kleben.
 * Wird auch von Gegnern und dem Begleiter benutzt.
 */
export function bewegeMitKollision(welt: Welt, pos: Vek, versatz: Vek, radius: number): void {
  const k = welt.karte;
  const zielX = pos.x + versatz.x;
  if (
    begehbar(k, zielX + Math.sign(versatz.x) * radius, pos.y - radius * 0.7) &&
    begehbar(k, zielX + Math.sign(versatz.x) * radius, pos.y + radius * 0.7)
  ) {
    pos.x = zielX;
  }
  const zielY = pos.y + versatz.y;
  if (
    begehbar(k, pos.x - radius * 0.7, zielY + Math.sign(versatz.y) * radius) &&
    begehbar(k, pos.x + radius * 0.7, zielY + Math.sign(versatz.y) * radius)
  ) {
    pos.y = zielY;
  }
}

export function spielerBewegung(welt: Welt, ctx: Ctx): void {
  const s = welt.spieler;
  if (s.tot) return;

  // Wirkende Fähigkeiten bremsen, halten aber nicht ganz an — sonst fühlt
  // sich die Automatik wie eine Blockade an.
  const bremse = s.wirktNoch > 0 ? 0.35 : 1;

  // `richtung` ist die EINZIGE Quelle. Auto-Lauf heißt nur: sie wird beim
  // Loslassen nicht geleert (siehe System `eingabe`).
  const richtung = s.anker ? vek(0, 0) : s.richtung;

  const gangart = tempo(welt, ctx.inhalte).spielerTempo;
  const versatz = vek(
    richtung.x * s.tempo * gangart * bremse * TAKT_DAUER + s.stoss.x * TAKT_DAUER,
    richtung.y * s.tempo * gangart * bremse * TAKT_DAUER + s.stoss.y * TAKT_DAUER,
  );
  bewegeMitKollision(welt, s.pos, versatz, s.radius);

  if (laenge(richtung) > 0.08) s.winkel = winkelVon(richtung);

  s.stoss.x *= 0.82;
  s.stoss.y *= 0.82;
  if (Math.abs(s.stoss.x) < 0.02) s.stoss.x = 0;
  if (Math.abs(s.stoss.y) < 0.02) s.stoss.y = 0;

  if (s.wirktNoch > 0) s.wirktNoch = Math.max(0, s.wirktNoch - TAKT_DAUER);
  for (const slot of s.slots) {
    if (slot.abklingen > 0) slot.abklingen = Math.max(0, slot.abklingen - TAKT_DAUER);
  }
}

export function spielerRegeneration(welt: Welt, ctx: Ctx): void {
  const s = welt.spieler;
  const r = ctx.inhalte.regelwerk.spieler;

  s.vorrat = Math.min(s.vorratMax, s.vorrat + s.vorratRegen * TAKT_DAUER);

  // Lebenskraft kommt nur außerhalb des Kampfes zurück — sie rettet niemanden
  // mitten im Gefecht, erspart aber das Zurücklaufen zum Händler.
  s.ohneSchadenSeit += TAKT_DAUER;
  if (s.ohneSchadenSeit >= r.lebenRegenAbSekunden && !s.tot) {
    s.leben = Math.min(s.lebenMax, s.leben + s.lebenMax * s.lebenRegen * TAKT_DAUER);
  }
}

export function spielerStaerkungen(welt: Welt, _ctx: Ctx): void {
  const s = welt.spieler;
  for (const st of s.staerkungen) st.verbleibend -= TAKT_DAUER;
  for (let i = s.staerkungen.length - 1; i >= 0; i--) {
    if ((s.staerkungen[i] as { verbleibend: number }).verbleibend <= 0) s.staerkungen.splice(i, 1);
  }
}

export function staerkungSchaden(s: Spieler): number {
  let f = 1;
  for (const st of s.staerkungen) f += st.schaden;
  return f;
}

export function staerkungMinderung(s: Spieler): number {
  let m = 0;
  for (const st of s.staerkungen) m += st.schadensminderung;
  return Math.min(0.8, m);
}
